/// <reference path="./global.d.ts"/>

import { IPosition, ISize } from '../domain/dimension';
import { IAppearence } from '../domain/style';
import { IFontStyle } from '../domain/font';
import { IEnvironment } from './../server/environment';
import { rotatePosition, getCenter, checksum, toFixedDecimal, pick } from '../../lib/utils';
import { INodeVisitor, visitAllNodes, visitAllChildren } from './visitor';
import { ICSSProperties, boxShadowsCSSValue, borderCSSValue } from './css-styles';
import { Matrix } from './matrix';
import { Color } from './color';
import * as N from './nodes';
import { createSVGLoaderImage } from '../domain/image';
import { IKeyframe } from '../domain/keyframe';
import { IRichTextRenderOption } from './rich-text';
import { NgZone } from '@angular/core';
import { IVersion } from '../domain/version';
import { ICollection } from '../domain/array';
import { IElement } from '../domain/element';
import { EventEmitter } from '../../lib/event-emitter';
import { FeedStore } from './feed-store';
import { SpanType, IText } from '../domain/text';
import { withinImageBounds, imageScaleFactor } from '../../lib/geom';
import { WidgetRenderer } from '../widget/widget-renderer';
import { isContentSpan } from './text-nodes';
import { getImageOptimizerUrl } from '../ad/utils/image-optimizer';
import { PromiseResolver, loadImagePromise } from '../../lib/promises';
import { isSafeUrl } from '../../lib/sanitizer';
import { createRichText } from './rich-text.factories';
import { isFirefox, isBlink } from '../ad/utils/browser';

type AppearenceStyles = Partial<IAppearence & ISize>;
type RendererEvents = 'textElementInteractionsStarted' | 'textElementInteractionsEnded' | 'texteditburst' | 'texteditstart' | 'texteditend' | 'texthistorychange' | 'click' | 'destroy';

export interface IRendererEnvironment extends IEnvironment {
    DESIGN_VIEW?: boolean;
}

interface ICachedCssStyles {
    styleRule: CSSStyleRule;
    rawStyleValue: CSSStyleDeclaration;
}

interface IRendererOption {
    ngZone?: NgZone;
    contentWindow?: Window;
    canvasSize?: ISize;
    elements?: ICollection<IElement>;
    versions?: ICollection<IVersion>;
    currentVersionId?: string;
    smallestCommonImageSizeMap?: SmallestCommonImageSizeMap;
    inDesignView?: boolean;
    zoom?: number;
}

export type SmallestCommonImageSizeMap = Map<string/*url*/, ISize>;

export class Renderer extends EventEmitter<RendererEvents> implements INodeVisitor {
    viewTree: N.ICreativeViewNode;
    canvasSize: ISize;
    styleRules = new Map<string, {
        /**
         * Keep track of previous and current values both
         * the raw and actual CSSStyleRule as css values get trimmed when
         * applied in some cases (e.g transform matrices)
         */
        previous: ICachedCssStyles,
        current: ICachedCssStyles
    }>();
    rootElement: HTMLDivElement;
    scopeId: string;
    time = 0;
    loop = 1;
    loopChanged = false;
    window: Window;

    // tslint:disable-next-line
    private static scopeIndex = 0;

    env: IRendererEnvironment;
    private styleSheet: CSSStyleSheet;
    private browserDocument: Document;
    private htmlElements = new Map<string, HTMLElement>();
    private fragment: DocumentFragment;
    private styleElement: HTMLStyleElement;
    private mountPoint: HTMLElement;
    private onlyRenderTextWithoutFontLoad: boolean;
    WidgetRenderer?: WidgetRenderer;
    storedViewElements = new Map<string, N.OneOfElements>();
    fallbackFonts = 'Helvetica, Arial, Tahoma, "Microsoft Yahei", 微软雅黑, STXihei, 华文细黑, sans-serif';
    feedStore?: FeedStore;
    destroyed = false;
    shouldRerender: boolean;
    webPSupport = false;
    preloadingElements = new Map<string, Promise<any>>();
    elements?: ICollection<IElement>;
    versions?: ICollection<IVersion>;
    currentVersionId?: string;
    private smallestCommonImageSizeMap?: SmallestCommonImageSizeMap;
    private inDesignView: boolean;

    constructor(
        public creativeDocument: N.ICreativeDataNode,
        option: IRendererOption = {}
    ) {
        super();
        this.elements = option.elements;
        this.versions = option.versions;
        this.inDesignView = option.inDesignView || false;
        this.window = option.contentWindow || window;
        this.browserDocument = this.window.document;
        this.styleElement = this.browserDocument.createElement('style');
        this.browserDocument.head!.appendChild(this.styleElement);
        this.styleSheet = this.styleElement.sheet as CSSStyleSheet;
        this.fragment = this.browserDocument.createDocumentFragment();
        this.viewTree = { kind: N.NodeKind.Creative, elements: [], width: creativeDocument.width, height: creativeDocument.height };
        this.smallestCommonImageSizeMap = option.smallestCommonImageSizeMap;
        if (option.canvasSize) {
            this.canvasSize = option.canvasSize;
        }
        if (typeof (window as any).process !== undefined) {
            const process = (window as any).process || { env: {} };
            this.env = process.env as unknown as IEnvironment;
        }
        if (this.env.STUDIO_JS && this.feedStore) {
            this.feedStore.on('dataChanged', this.updateViewElementValues);
        }
    }

    visitCreative(creative: N.ICreativeDataNode): void {
        this.scopeId = `b${(this.constructor as any).scopeIndex}`;
        this.rootElement = this.browserDocument.createElement('div');
        this.rootElement.setAttribute('data-banner', ''); // TODO: Remove this after image renderer is updated
        this.rootElement.setAttribute('data-creative', '');
        this.htmlElements.set(this.scopeId, this.rootElement);
        this.viewTree.width = creative.width;
        this.viewTree.height = creative.height;
        this.viewTree.radius = creative.radius;
        this.viewTree.fill = creative.fill;
        this.viewTree.border = creative.border;
        this.viewTree.__rootElement = this.rootElement;

        this.insertStyle(this.scopeId, {
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            // perspectiveOrigin: '50% 50%',
            // perspective: '500px',
            // background: creative.fill ? creative.fill.toString() : 'transparent',
            position: 'relative',
            fontFamily: 'Helvetica, Arial, Tahoma, "Microsoft Yahei", 微软雅黑, STXihei, 华文细黑, sans-serif'
        });
        this.setBackgroundElement(this.viewTree, { width: this.canvasSize.width, height: this.canvasSize.height });
        this.fragment.appendChild(this.rootElement);
        Renderer.scopeIndex++;
        (this.window as any).creative = creative;
    }

    visitRectangle(rectangle: N.IRectangleElementDataNode, disablePosition: boolean = false): N.IRectangleElement {
        const viewElement = this.createViewElementFromDataElement<N.IRectangleElement>(rectangle);
        viewElement.__perspectiveElement = this.createPerspectiveElement(viewElement);
        viewElement.__rootElement = this.createElement('div', viewElement) as HTMLDivElement;
        this.insertStyle(viewElement.elementCid!, {
            ...this.getDefaultStyle(viewElement)
        });
        this.setBackgroundElement(viewElement, undefined, undefined, disablePosition);

        this.viewTree.elements.unshift(viewElement);

        viewElement.__rootElement.addEventListener('click', this.onClick);

        return viewElement;
    }

    visitEllipse(ellipse: N.IEllipseElementDataNode, disablePosition: boolean = false): N.IEllipseElement {
        const viewElement = this.createViewElementFromDataElement<N.IEllipseElement>(ellipse);
        viewElement.__perspectiveElement = this.createPerspectiveElement(viewElement);
        viewElement.__rootElement = this.createElement('div', viewElement) as HTMLDivElement;
        this.insertStyle(viewElement.elementCid!, {
            ...this.getDefaultStyle(viewElement)
        });
        this.setBackgroundElement(viewElement, undefined, undefined, disablePosition);

        this.viewTree.elements.unshift(viewElement);

        viewElement.__rootElement.addEventListener('click', this.onClick);

        return viewElement;
    }

    visitText(text: N.ITextElementDataNode, disablePosition: boolean = false): N.ITextElement {
        return this.visitTextNode(text, disablePosition) as N.ITextElement;
    }

    visitButton(button: N.IButtonElementDataNode, disablePosition: boolean = false): N.IButtonElement {
        return this.visitTextNode(button, disablePosition) as N.IButtonElement;
    }

    visitTextNode(text: N.OneOfTextDataNodes, disablePosition: boolean = false): N.OneOfTextNodes {
        const viewElement = this.createViewElementFromDataElement<N.OneOfTextNodes>(text);
        viewElement.__perspectiveElement = this.createPerspectiveElement(viewElement);
        const htmlElement = this.createElement('div', viewElement) as HTMLDivElement;
        htmlElement.classList.add('text');

        this.insertStyle(viewElement.elementCid!, {
            ...this.getDefaultStyle(viewElement)
        });

        viewElement.__rootElement = htmlElement;

        this.setBackgroundElement(viewElement, undefined, undefined, disablePosition);
        if (viewElement.font) {
            for (const [_, style] of text.characterStyles.entries()) {
                if (style.font) {
                    this.injectFontFace(style.font);
                }
            }
            this.injectFontFace(viewElement.font);
        }

        if ((self as any).inServer) {
            // this.setTextContent(viewElement, );
        }
        else {
            this.renderText(viewElement);
        }

        this.viewTree.elements.unshift(viewElement);

        return viewElement;
    }

    visitImage(image: N.IImageElementDataNode): N.IImageElement {
        const viewElement = this.createViewElementFromDataElement<N.IImageElement>(image);
        viewElement.__perspectiveElement = this.createPerspectiveElement(viewElement);
        viewElement.__rootElement = this.createElement('div', viewElement as any) as HTMLDivElement;
        this.insertStyle(viewElement.elementCid!, {
            ...this.getDefaultStyle(viewElement),
        });
        this.setBackgroundElement(viewElement);

        this.viewTree.elements.unshift(viewElement);

        const onImageClick = (event: MouseEvent): void => {
            let deepLinkUrl = '';
            if (image.feed && this.feedStore) {
                deepLinkUrl = this.feedStore.getFeedValue(image.feed, viewElement.id).targetUrl || '';
            }
            this.onClick(event, deepLinkUrl);
        };

        this.on('destroy', () => {
            if (viewElement.__rootElement) {
                viewElement.__rootElement.removeEventListener('click', onImageClick);
            }
        });

        viewElement.__rootElement.addEventListener('click', onImageClick);

        return viewElement;
    }

    visitWidget(widgetDataElement: N.IWidgetElementDataNode): N.IWidgetElement {
        const viewElement = this.createViewElementFromDataElement<N.IWidgetElement>(widgetDataElement);
        viewElement.__perspectiveElement = this.createPerspectiveElement(viewElement);
        viewElement.__rootElement = this.createElement('div', viewElement) as HTMLDivElement;

        this.insertStyle(viewElement.elementCid!, {
            ...this.getDefaultStyle(viewElement),
        });

        this.setBackgroundElement(viewElement);

        const creativeElement = viewElement.__rootElement.parentNode;

        if (creativeElement) {
            if (!creativeElement.querySelector(`#widget-${widgetDataElement.id}`)) {
                this.WidgetRenderer!.createWidget(viewElement, widgetDataElement);
            }
        }

        this.viewTree.elements.unshift(viewElement);

        return viewElement;
    }

    private onClick = (event: MouseEvent, deepLinkUrl?: string): void => {
        if (window.bfstudio && !window.bfstudio.inPreviewMode) {
            return;
        }
        if (!this.env.STUDIO_JS) {
            event.stopPropagation();
        }
        this.emit('click', [event, deepLinkUrl]);
    }

    visitGroup(group: N.IGroupElementDataNode): void { }

    getCssDecorations(decorations?: string): string {
        if (decorations) {
            return decorations.split(' ').map((decoration) => {
                switch (decoration) {
                    case 'strikethrough':
                        return 'line-through';
                    default:
                        return decoration;
                }
            }).join(' ');
        }
        return '';
    }

    setBackgroundElement(node: N.ISVGBackgroundNode, overrides?: AppearenceStyles, shape?: 'rect' | 'ellipse' | 'image', disablePosition: boolean = false): void {

        // TODO: Ugly, is there another way?
        let styles = node;
        if (overrides) {
            styles = { ...node, ...overrides };
        }

        // If not passed, detect shape
        if (!shape) {
            switch (node.kind) {
                case N.NodeKind.Ellipse:
                    shape = 'ellipse';
                    break;
                case N.NodeKind.Image:
                    shape = 'image';
                    break;
                default:
                    shape = 'rect';
            }
        }

        // Dont re-render if nothing has changed
        const checksum = this.getSVGBackgroundChecksum(styles);
        if (node.__backgroundElement) {
            if (node.__backgroundElement.getAttribute('data-checksum') === checksum) {
                return;
            }
        }

        const cid = N.isElementNode(node) ? node.elementCid : 'creative';
        const document = this.browserDocument;
        const baseUrl = document.querySelector('head base') ? this.window.location.href.replace(this.window.location.hash, '') : '';
        const shapeId = (cid || '') + '-shape-' + Math.floor(Math.random() * 1000000);
        const groupId = (cid || '') + '-group-' + Math.floor(Math.random() * 1000000);
        const svgNamespace = 'http://www.w3.org/2000/svg';
        const xlinkNamespace = 'http://www.w3.org/1999/xlink';
        const groupElement = document.createElementNS(svgNamespace, 'g');
        const shapeElement = document.createElementNS(svgNamespace, shape === 'ellipse' ? 'ellipse' : 'rect');
        const defsElement = document.createElementNS(svgNamespace, 'defs');
        const borderThickness = styles.border ? styles.border.thickness : 0;
        const svgElement = document.createElementNS(svgNamespace, 'svg');
        let imageSvgElement: SVGElement;
        const width = styles.width;
        const height = styles.height;
        const radius = Math.round(Math.min(styles.radius || 0, width / 2, height / 2));
        const imageOptimizerOrigin = this.env?.IMAGE_OPTIMIZER_ORIGIN;

        svgElement.setAttribute('xmlns', svgNamespace); // Needed by serializer
        svgElement.setAttribute('data-checksum', checksum);
        svgElement.setAttribute('width', width + '');
        svgElement.setAttribute('height', height + '');
        svgElement.style.position = disablePosition ? 'unset' : 'absolute';
        svgElement.style.left = '0';
        svgElement.style.top = '0';
        svgElement.style.width = '100%';
        svgElement.style.height = '100%';
        svgElement.style.overflow = 'visible';
        svgElement.style.zIndex = '-1'; // Needs to be z-index -1 because text can disappear otherwise.
        svgElement.style.pointerEvents = 'none';

        // Hacks to trigger Anti-aliasing
        svgElement.style.backfaceVisibility = 'hidden';
        svgElement.style.outline = '1px solid transparent';

        shapeElement.id = shapeId;
        shapeElement.setAttribute('fill', 'transparent');

        groupElement.id = groupId;
        groupElement.appendChild(shapeElement);

        if (shape === 'ellipse') {
            // Center
            shapeElement.setAttribute('cx', width / 2 + '');
            shapeElement.setAttribute('cy', height / 2 + '');

            // Radius
            shapeElement.setAttribute('rx', width / 2 + '');
            shapeElement.setAttribute('ry', height / 2 + '');
        }
        else if (shape === 'rect' || shape === 'image') {
            shapeElement.setAttribute('width', width + '');
            shapeElement.setAttribute('height', height + '');
            shapeElement.setAttribute('rx', radius + '');
        }

        // Add image element
        if (shape === 'image') {
            let url = '';
            const imageElement = node as N.IImageElement;
            const feed = imageElement.feed;
            const fitOption = imageElement.fitOption;
            if (feed) {
                const { value } = this.feedStore!.getFeedValue(feed, imageElement.id, node as N.IElementNode);

                // Sometimes the feeded image is not set
                if (value && typeof value === 'string') {

                    // Optimize image in MV
                    if (!this.inDesignView) {
                        url = getImageOptimizerUrl(value, {
                            width,
                            height,
                            fitOption: fitOption === 'cover' ? 'cover' : 'contain', // Feeded images should always keep ratio
                            webpSupport: this.webPSupport,
                            imageOptimizerOrigin,
                            quality: imageElement.quality // This needs to be passed to work in output for now
                        });
                    }
                    // In DV, just check it's a safe url
                    else if (isSafeUrl(url)) {
                        url = value;
                    }
                }
            }
            else {
                const imageAsset = imageElement.imageAsset!;
                const size = this.smallestCommonImageSizeMap?.get(imageAsset.id);
                // Only in MV
                if (size) {
                    url = getImageOptimizerUrl(imageAsset.url, {
                        ...size,
                        fitOption,
                        webpSupport: this.webPSupport,
                        imageOptimizerOrigin,
                        quality: imageElement.quality
                    });
                }
                // In output imageAsset.url is changed to the optimized value in animated-creative.
                else {
                    url = imageAsset!.url!;
                }
            }

            /**
             * xMidYMid meet === contain
             * xMidYMid slice === cover
             * none === 100%
             */
            const fillOption = this.getSvgFillOption(imageElement);
            let fitX = 0;
            let fitY = 0;

            const isCover = imageElement.fitOption === 'cover';
            const fitPositionX = isCover && imageElement.fitPositionX ? imageElement.fitPositionX : 0;
            const fitPositionY = isCover && imageElement.fitPositionY ? imageElement.fitPositionY : 0;
            if (isCover) {
                shapeElement.style.overflow = 'hidden';
            }
            if (!feed) {
                const { width: newWidth, height: newHeight } = imageScaleFactor(node.imageAsset!.width!, node.width, node.imageAsset!.height!, node.height);

                const bounds = withinImageBounds(fitPositionX!, fitPositionY!, newWidth, node.width, newHeight, node.height);
                fitX = bounds.fitX;
                fitY = bounds.fitY;
            }
            if (this.env && !this.env.STUDIO_JS && this.env.IMAGE_OPTIMIZER_ORIGIN && imageElement.quality !== 100) {
                /** @description
                 * We dont need to position a optimized image
                 */
                fitX = 0;
                fitY = 0;
            }

            const imageWidth = Math.max((width) + Math.abs(fitX), 0);
            const imageHeight = Math.max((height) + Math.abs(fitY), 0);

            const imageX = (fitX < 0 ? fitX : 0);
            const imageY = (fitY < 0 ? fitY : 0);

            imageSvgElement = document.createElementNS(svgNamespace, 'image');

            imageSvgElement.setAttribute('width', imageWidth + '');
            imageSvgElement.setAttribute('height', imageHeight + '');

            imageSvgElement.setAttribute('x', imageX + '');
            imageSvgElement.setAttribute('y', imageY + '');

            imageSvgElement.setAttribute('preserveAspectRatio', fillOption);
            imageSvgElement.setAttribute('rx', radius + '');

            if (url) {
                if (!this.preloadingElements.has((node as N.IImageElement).id)) {
                    if (isBlink || isFirefox) {
                        const promiseResolver = new PromiseResolver();
                        this.preloadingElements.set((node as N.IImageElement).id, promiseResolver.promise);
                        imageSvgElement.addEventListener('load', () => promiseResolver.resolve());
                        imageSvgElement.addEventListener('error', () => promiseResolver.reject());
                    }
                    // Load events on SVGs doesn't work in certain browsers
                    else {
                        const preloadPromise = loadImagePromise(url);
                        this.preloadingElements.set((node as N.IImageElement).id, preloadPromise);
                    }
                }
                imageSvgElement.setAttributeNS(xlinkNamespace, 'xlink:href', url);
                imageSvgElement.setAttribute('src', url);
            }

            groupElement.appendChild(imageSvgElement);

            if (imageElement.imageAsset && imageElement.imageAsset!.__loading) {
                const loadingElement = createSVGLoaderImage({ width, height });
                groupElement.appendChild(loadingElement);
            }
        }

        // Fill
        if (styles.fill) {
            const fill = styles.fill;
            const stops = fill.stops || [];

            // See more here: https://codepen.io/NV/pen/jcnmK
            if (styles.fill.type === 'linear-gradient' && stops.length) {
                const gradientId = `${cid || 'unset'}-gradient-${Math.floor(Math.random() * 1000000)}`;
                const gradientElement = document.createElementNS(svgNamespace, 'linearGradient');
                gradientElement.id = gradientId;
                const from = fill.start.position;
                const to = fill.end.position;

                if (!from || !to) {
                    return;
                }

                gradientElement.setAttribute('x1', from.x * width + '');
                gradientElement.setAttribute('y1', from.y * height + '');
                gradientElement.setAttribute('x2', to.x * width + '');
                gradientElement.setAttribute('y2', to.y * height + '');
                gradientElement.setAttribute('gradientUnits', 'userSpaceOnUse');

                stops.forEach((stop) => {
                    const stopElement = document.createElementNS(svgNamespace, 'stop');
                    const color = stop.color;
                    stopElement.setAttribute('offset', stop.offset + '%');
                    stopElement.setAttribute('stop-color', color.toHEX());
                    stopElement.setAttribute('stop-opacity', ((color.alpha / 100) * (fill!.alpha / 100)).toString());
                    gradientElement.appendChild(stopElement);
                });
                defsElement.appendChild(gradientElement);

                // FIX FOR SAFARI
                if (navigator.userAgent.includes('Safari') && navigator.vendor.includes('Apple')) {
                    shapeElement.setAttribute('fill', `url('${baseUrl}#${gradientId}')`);
                }
                else {
                    shapeElement.setAttribute('fill', `url(#${gradientId})`);
                }
            } else {
                shapeElement.setAttribute('fill', fill.toString());
            }
        }

        // Border
        if (borderThickness || radius || (shape === 'image' && (node as N.IImageElement).fitOption === 'cover')) {
            let borderElement = shapeElement;
            // Odd width border fix. Anti-aliasing.
            svgElement.style.boxShadow = `0 0 ${borderThickness}px transparent`;

            // Image is handled differently (need to be clipped and border is on the outside)
            if (shape === 'image') {
                const borderId = (cid || '') + '-border-' + Math.floor(Math.random() * 1000000);
                const clipId = (cid || '') + '-clip-' + Math.floor(Math.random() * 1000000);
                borderElement = document.createElementNS(svgNamespace, 'rect');
                borderElement.setAttribute('width', width + '');
                borderElement.setAttribute('height', height + '');
                borderElement.setAttribute('x', 0 + '');
                borderElement.setAttribute('y', 0 + '');
                borderElement.setAttribute('rx', radius + '');
                borderElement.setAttribute('fill', 'transparent');

                borderElement.id = borderId;

                const clipPathElement = document.createElementNS(svgNamespace, 'clipPath');
                clipPathElement.id = clipId;

                const clipUseElement = document.createElementNS(svgNamespace, 'use');
                clipUseElement.setAttributeNS(xlinkNamespace, 'xlink:href', `${baseUrl}#${borderId}`);
                clipPathElement.appendChild(clipUseElement);

                const borderUseElement = document.createElementNS(svgNamespace, 'use');
                borderUseElement.setAttributeNS(xlinkNamespace, 'xlink:href', `${baseUrl}#${borderId}`);

                defsElement.appendChild(borderElement);
                defsElement.appendChild(clipPathElement);
                groupElement.appendChild(borderUseElement);

                imageSvgElement!.setAttribute('clip-path', `url(${baseUrl}#${clipId})`);
            }

            if (styles.border) {
                borderElement.setAttribute('stroke', styles.border.color);
                borderElement.setAttribute('stroke-width', styles.border.thickness + '');
                borderElement.setAttribute('stroke-width', styles.border.thickness + '');
                borderElement.setAttribute('stroke-linejoin', 'square');

                if (styles.border.type === 'dashed') {
                    borderElement.setAttribute('stroke-linecap', 'butt');
                    borderElement.setAttribute('stroke-dasharray', borderThickness * 3 + '');
                }
                else if (styles.border.type === 'dotted') {
                    borderElement.setAttribute('stroke-linecap', 'round');
                    borderElement.setAttribute('stroke-dasharray', `1, ${borderThickness * 2}`);
                }
            }
        }

        // SVG Shadows: TODO: support <base href="/">
        if (styles.shadows) {

            // Apply shadows backwards to get the correct z index...
            styles.shadows.slice().reverse().forEach(shadow => {

                // Note that the CSS drop-shadow filter doesn't support spread
                const filterId = (cid || '') + '-shadow-' + Math.floor(Math.random() * 1000000);
                const filterElement = document.createElementNS(svgNamespace, 'filter');
                filterElement.id = filterId;
                filterElement.setAttribute('color-interpolation-filters', 'sRGB');

                // TODO: Maybe do theses dynamic?
                filterElement.setAttribute('x', '-100%');
                filterElement.setAttribute('y', '-100%');
                filterElement.setAttribute('width', '300%');
                filterElement.setAttribute('height', '300%');

                const useElement = document.createElementNS(svgNamespace, 'use');
                useElement.setAttributeNS(xlinkNamespace, 'xlink:href', `${baseUrl}#${groupId}`); // Will not render <use> tag if this isn't set
                useElement.setAttribute('x', shadow.offsetX + '');
                useElement.setAttribute('y', shadow.offsetY + '');
                useElement.style.transform = `scaleX(${1 + shadow.spread / node.width}) scaleY(${1 + shadow.spread / node.height})`;

                // TODO: Using stroke-width instead of scale will give a better spread. But for doing this the original element also need to be shown as a use tag.
                // useElement.style.strokeWidth = borderThickness / 2 + shadow.spread * 2 + 'px';
                useElement.setAttribute('filter', `url(${baseUrl}#${filterId})`);
                useElement.style.transformOrigin = '50% 50%';

                // Only way that works in Edge, example: https://codepen.io/mullany/pen/sJopz
                const color = Color.parse(shadow.color);
                const colorMatrix = `0 0 0 0 ${color.red / 255} 0 0 0 0 ${color.green / 255} 0 0 0 0 ${color.blue / 255} 0 0 0 ${color.alpha / 100} 0`;

                filterElement.innerHTML = `
                    <feColorMatrix type="matrix" values="${ colorMatrix}" in="SourceGraphic" result="${filterId}_color"></feColorMatrix>
                    <feGaussianBlur in="${filterId}_color" stdDeviation="${shadow.blur}" result="${filterId}_blur"></feGaussianBlur>
                `;

                // Add elements
                defsElement.appendChild(filterElement);
                svgElement!.appendChild(useElement);
            });
        }

        // Remove current svg
        if (node.__backgroundElement && node.__backgroundElement.parentNode) {
            node.__backgroundElement.parentNode.removeChild(node.__backgroundElement);
            node.__backgroundElement = undefined;
        }

        svgElement.appendChild(defsElement);
        svgElement.appendChild(groupElement);
        // svgElement.appendChild(shapeElement);
        node.__rootElement!.appendChild(svgElement);
        node.__backgroundElement = svgElement;
    }

    getSvgFillOption = (node: N.IImageElement): string => {
        if (!node.fitOption) {
            if (node.ratio) {
                return 'xMidYMid slice';
            }
            else if (!node.ratio) {
                return 'none';
            }

            return 'xMidYMid meet';
        }
        const options = {
            contain: 'xMidYMid meet',
            cover: 'xMidYMid slice',
            distort: 'none'
        };
        return options[node.fitOption];
    }

    renderText(textElement: N.OneOfTextNodes): void {
        let richTextRenderer = textElement.__richTextRenderer;

        if (richTextRenderer) {
            richTextRenderer.rerender();
            return;
        }
        const options: IRichTextRenderOption = {
            feedStore: this.feedStore!,
            element: textElement.__data as N.OneOfTextDataNodes,
            viewElement: textElement,
            skipFontLoad: this.onlyRenderTextWithoutFontLoad,
        };
        const style = pick(textElement,
            'width',
            'height',
            ...N.textProperties
        );
        const text: IText = { spans: textElement.content.spans, style };
        richTextRenderer = textElement.__richTextRenderer = createRichText(text, textElement.__rootElement!, options);
        richTextRenderer.init();
        richTextRenderer.on('editburst', () => this.emit('texteditburst'));
        richTextRenderer.on('editstart', () => this.emit('texteditstart'));
        richTextRenderer.on('editend', (change, _, command, data) => this.emit('texteditend', [change, undefined, command, data]));
        richTextRenderer.on('historychange', () => this.emit('texthistorychange'));

        const onElementInteractionsStarted = () => {
            this.emit('textElementInteractionsStarted', [richTextRenderer]);
        };
        const onElementInteractionsEnded = () => {
            this.emit('textElementInteractionsEnded', [richTextRenderer]);
        };
        const onElementDestroyed = () => {
            richTextRenderer.off('interactionsStarted', onElementInteractionsStarted);
            richTextRenderer.off('interactionsEnded', onElementInteractionsEnded);
            richTextRenderer.off('destroyed', onElementDestroyed);
        };

        const onSpanClicked = (event: MouseEvent, deepLinkUrl?: string) => {
            this.onClick(event, deepLinkUrl);
        };

        const onTextClick = (event: MouseEvent) => {
            let deepLinkUrl = '';
            try {
                if (this.feedStore) {
                    for (const span of textElement.__richTextRenderer.text.spans) {
                        if (isContentSpan(span) && span.style.variable) {
                            const feedValue = this.feedStore.getFeedValue(span.style.variable, span.style.variable.spanId!);
                            deepLinkUrl = feedValue.targetUrl!;
                            break;
                        }
                    }
                }
            } catch (e) { }
            this.onClick(event, deepLinkUrl);
        };

        textElement.__rootElement!.addEventListener('click', onTextClick);

        this.on('destroy', () => {
            if (textElement.__rootElement) {
                textElement.__rootElement!.removeEventListener('click', onTextClick);
            }
        });

        // Try to remove listeners, renderText is triggered by undo/redo
        richTextRenderer.off('interactionsStarted', onElementInteractionsStarted);
        richTextRenderer.off('interactionsEnded', onElementInteractionsEnded);
        richTextRenderer.off('destroyed', onElementDestroyed);
        richTextRenderer.off('spanClicked', onSpanClicked);

        richTextRenderer.on('interactionsStarted', onElementInteractionsStarted);
        richTextRenderer.on('interactionsEnded', onElementInteractionsEnded);
        richTextRenderer.on('spanClicked', onSpanClicked);
        richTextRenderer.on('destroyed', onElementDestroyed);
    }

    getKeyframeValueAtTime(keyframes: IKeyframe[], time: number = 0): any {
        return keyframes[0].value;
    }

    setViewElementValues(element: N.OneOfDataElements, time: number, setStyle: boolean = true): void {
        this.time = time;
        const viewElement = this.storedViewElements.get(element.id)!;

        if (!viewElement) {
            throw new Error(`Could not find a view node with id '${element.id}'`);
        }

        const x = N.getAnimatablePropertyValue(element, 'x', time, this.canvasSize);
        const y = N.getAnimatablePropertyValue(element, 'y', time, this.canvasSize);
        const width = N.getAnimatablePropertyValue(element, 'width', time, this.canvasSize);
        const height = N.getAnimatablePropertyValue(element, 'height', time, this.canvasSize);
        viewElement.x = x;
        viewElement.y = y;
        viewElement.width = width;
        viewElement.height = height;

        viewElement.scaleX = N.getAnimatablePropertyValue(element, 'scaleX', time, this.canvasSize);
        viewElement.scaleY = N.getAnimatablePropertyValue(element, 'scaleY', time, this.canvasSize);
        viewElement.fill = N.getAnimatablePropertyValue(element, 'fill', time, this.canvasSize);

        viewElement.originX = N.getAnimatablePropertyValue(element, 'originX', time, this.canvasSize);
        viewElement.originY = N.getAnimatablePropertyValue(element, 'originY', time, this.canvasSize);
        viewElement.rotationX = N.getAnimatablePropertyValue(element, 'rotationX', time, this.canvasSize);
        viewElement.rotationY = N.getAnimatablePropertyValue(element, 'rotationY', time, this.canvasSize);
        viewElement.rotationZ = N.getAnimatablePropertyValue(element, 'rotationZ', time, this.canvasSize);
        viewElement.mirrorX = N.getAnimatablePropertyValue(element, 'mirrorX', time, this.canvasSize);
        viewElement.mirrorY = N.getAnimatablePropertyValue(element, 'mirrorY', time, this.canvasSize);
        viewElement.opacity = N.getAnimatablePropertyValue(element, 'opacity', time, this.canvasSize);
        viewElement.border = N.getAnimatablePropertyValue(element, 'border', time, this.canvasSize);
        viewElement.shadows = N.getAnimatablePropertyValue(element, 'shadows', time, this.canvasSize);
        viewElement.radius = N.getAnimatablePropertyValue(element, 'radius', time, this.canvasSize);

        if (viewElement.hidden !== element.hidden) {
            viewElement.hidden = element.hidden;
        }

        if (viewElement.kind === N.NodeKind.Image) {
            viewElement.fitOption = N.getAnimatablePropertyValue(element, 'fitOption', time, this.canvasSize);
            viewElement.quality = N.getAnimatablePropertyValue(element, 'quality', time, this.canvasSize);
            if (viewElement.fitOption === 'cover') {
                viewElement.fitPositionX = N.getAnimatablePropertyValue(element, 'fitPositionX', time, this.canvasSize);
                viewElement.fitPositionY = N.getAnimatablePropertyValue(element, 'fitPositionY', time, this.canvasSize);
            }
        }

        if ((N.isTextNode(viewElement))) {
            viewElement.textColor = N.getAnimatablePropertyValue(element, 'textColor', time, this.canvasSize);
            viewElement.textShadows = N.getAnimatablePropertyValue(element, 'textShadows', time, this.canvasSize);
            viewElement.underline = N.getAnimatablePropertyValue(element, 'underline', time, this.canvasSize);
            viewElement.strikethrough = N.getAnimatablePropertyValue(element, 'strikethrough', time, this.canvasSize);
            viewElement.uppercase = N.getAnimatablePropertyValue(element, 'uppercase', time, this.canvasSize);
            viewElement.textOverflow = N.getAnimatablePropertyValue(element, 'textOverflow', time, this.canvasSize);
            viewElement.lineHeight = N.getAnimatablePropertyValue(element, 'lineHeight', time, this.canvasSize);
            viewElement.fontSize = N.getAnimatablePropertyValue(element, 'fontSize', time, this.canvasSize);
            viewElement.maxRows = N.getAnimatablePropertyValue(element, 'maxRows', time, this.canvasSize);
            viewElement.padding = N.getAnimatablePropertyValue(element, 'padding', time, this.canvasSize);
            viewElement.characterSpacing = N.getAnimatablePropertyValue(element, 'characterSpacing', time, this.canvasSize);
            viewElement.horizontalAlignment = N.getAnimatablePropertyValue(element, 'horizontalAlignment', time, this.canvasSize);
            viewElement.verticalAlignment = N.getAnimatablePropertyValue(element, 'verticalAlignment', time, this.canvasSize);
        }

        if (setStyle) {
            const { current: currentStyle, previous: previousStyle } = this.styleRules.get(viewElement.elementCid!)!;
            const styles = this.getDefaultStyle(viewElement);
            currentStyle.rawStyleValue = styles;
            const {
                current: currentPerspectiveRule,
                previous: previousPerspectiveRule
            } = this.styleRules.get('p-' + viewElement.elementCid!)!;
            const perspectiveStyles = this.getPerspectiveStyle(viewElement);
            currentPerspectiveRule.rawStyleValue = perspectiveStyles;

            let sizeChanged = false;

            /**
             * Perspective rules
             */

            const zIndex = this.getZIndex(viewElement) + '';
            if (previousPerspectiveRule.styleRule.style.zIndex !== zIndex) {
                previousPerspectiveRule.styleRule.style.zIndex = zIndex;
            }
            if (previousPerspectiveRule.styleRule.style.perspective !== perspectiveStyles.perspective) {
                previousPerspectiveRule.styleRule.style.perspective = perspectiveStyles.perspective;
            }

            if (previousPerspectiveRule.rawStyleValue.perspectiveOrigin !== currentPerspectiveRule.rawStyleValue.perspectiveOrigin) {
                previousPerspectiveRule.styleRule.style.perspectiveOrigin = perspectiveStyles.perspectiveOrigin;
            }

            previousPerspectiveRule.rawStyleValue = currentPerspectiveRule.rawStyleValue;
            previousPerspectiveRule.styleRule = currentPerspectiveRule.styleRule;

             /**
             * End perspective rules
             */

            if (previousStyle.rawStyleValue.visibility !== currentStyle.rawStyleValue.visibility) {
                previousStyle.styleRule.style.visibility = currentStyle.rawStyleValue.visibility;
            }

            if (previousStyle.rawStyleValue.backfaceVisibility !== currentStyle.rawStyleValue.backfaceVisibility && currentStyle.rawStyleValue.backfaceVisibility) {
                previousStyle.styleRule.style.backfaceVisibility = currentStyle.rawStyleValue.backfaceVisibility;
            }

            if (previousStyle.rawStyleValue.transform !== currentStyle.rawStyleValue.transform) {
                previousStyle.styleRule.style.transform = currentStyle.rawStyleValue.transform;
            }

            if (previousStyle.rawStyleValue.width !== currentStyle.rawStyleValue.width) {
                previousStyle.styleRule.style.width = currentStyle.rawStyleValue.width;
                sizeChanged = true;
            }

            if (previousStyle.rawStyleValue.height !== currentStyle.rawStyleValue.height) {
                previousStyle.styleRule.style.height = currentStyle.rawStyleValue.height;
                sizeChanged = true;
            }

            if (previousStyle.rawStyleValue.opacity !== currentStyle.rawStyleValue.opacity) {
                previousStyle.styleRule.style.opacity = currentStyle.rawStyleValue.opacity;
            }

            previousStyle.rawStyleValue = currentStyle.rawStyleValue;
            previousStyle.styleRule = currentStyle.styleRule;

            if (N.isTextNode(viewElement)) {
                if (sizeChanged || (this.shouldRerender && viewElement.__richTextRenderer.feedStore)) {
                    const elementIsFeeded = viewElement.__richTextRenderer.feedStore.elements.get(viewElement.id) ||
                        viewElement.__richTextRenderer.text.spans
                            .find(span => span.type === SpanType.Variable);

                    if (elementIsFeeded || sizeChanged) {
                        viewElement.__richTextRenderer.rerender(viewElement);
                    }
                }
            }

            if (viewElement.kind === N.NodeKind.Image) {
                if (viewElement.feed) {
                    if (!this.feedStore!.getFeed(viewElement.feed.id)) {
                        this.feedStore!.add(viewElement.feed.id);
                    }
                    viewElement.feed = (element as N.IImageElementDataNode).feed = viewElement.feed;
                    viewElement.imageAsset = (element as N.IImageElementDataNode).imageAsset = undefined;
                }
                else if (viewElement.imageAsset) {
                    viewElement.imageAsset = (element as N.IImageElementDataNode).imageAsset = viewElement.imageAsset;
                    viewElement.feed = (element as N.IImageElementDataNode).feed = undefined;
                }
            }

            this.setBackgroundElement(viewElement);
        }
    }

    setAllViewElementsValues(time: number, shouldRerender?: boolean): void {
        if (this.destroyed) {
            return;
        }

        if (shouldRerender) {
            this.shouldRerender = shouldRerender;
        }

        N.forEachDataElement(this.creativeDocument, element => {
            this.setViewElementValues(element, time);
        });

        this.shouldRerender = false;
    }

    updateViewElementValues = (): void => {
        this.setAllViewElementsValues(this.time);
    }

    rerender(creative: N.CreativeDataNode, currentVersionId?: string, time: number = 0): void {
        this.onlyRenderTextWithoutFontLoad = false;
        this.creativeDocument = creative;
        this.currentVersionId = currentVersionId;
        this.time = time;
        this.clearElementHandlers();
        this.viewTree = { kind: N.NodeKind.Creative, elements: [] } as any;

        this.fragment = this.browserDocument.createDocumentFragment();

        N.resetNodeCid();
        this.mountPoint.innerHTML = '';

        this.removeStyles();
        visitAllNodes(this.creativeDocument, this, false);
        this.rootElement.addEventListener('click', this.onClick);

        this.insertStyles();

        this.setZIndexStyleOnAllNodes();

        this.mountPoint.appendChild(this.fragment);
    }

    render(mountPoint: HTMLElement, canvasSize?: ISize, time: number = 0, resetNodeCid: boolean = true, dependencies?: IDependency[], onlyRenderTextWithoutFontLoad: boolean = false): void {
        this.time = time;
        this.onlyRenderTextWithoutFontLoad = onlyRenderTextWithoutFontLoad;
        const style = this.window.getComputedStyle(mountPoint);
        this.canvasSize = canvasSize || {
            width: parseInt(style.getPropertyValue('width'), 10),
            height: parseInt(style.getPropertyValue('height'), 10),
        };

        if (resetNodeCid) {
            N.resetNodeCid();
        }

        mountPoint.innerHTML = '';
        if (canvasSize) {
            mountPoint.style.width = canvasSize.width + 'px';
            mountPoint.style.height = canvasSize.height + 'px';
            mountPoint.style.transformOrigin = '0px 0px';
        }

        this.viewTree = { kind: N.NodeKind.Creative, elements: [] } as any;

        this.removeStyles();

        if (this.destroyed) {
            return;
        }
        // Work around to get the cycle of WidgetRenderer's initialization correct
        this.visitCreative(this.creativeDocument);

        if (dependencies) {
            for (const dependency of dependencies) {
                const ctor = dependency.constructor;
                this[dependency.name] = new ctor(...dependency.arguments);
            }
        }

        visitAllChildren(this.creativeDocument.elements, this, false, onlyRenderTextWithoutFontLoad);
        this.rootElement.addEventListener('click', this.onClick);
        this.insertStyles();

        for (const element of this.creativeDocument.elements) {
            if (onlyRenderTextWithoutFontLoad) {
                if (!N.isTextNode(element)) {
                    continue;
                }
            }
            const viewElement = this.storedViewElements.get(element.id)!;
            const styleRule = this.styleRules.get(viewElement.elementCid!)!.current.styleRule;
            const willChange: string[] = [];
            for (let i = 0; i < styleRule.style.length; i++) {
                if (!!styleRule.style[i].match(/(transform|opacity|perspectiveOrigin)/)) {
                    willChange.push(styleRule.style[i]);
                }
            }
            styleRule.style.willChange = willChange.join(', ');
        }

        this.setZIndexStyleOnAllNodes();
        mountPoint.appendChild(this.fragment);
        this.mountPoint = mountPoint;
    }

    findElementAtPosition(element: N.OneOfGroupNode, position: IPosition): N.OneOfElements | undefined {
        for (let i = 0; i < this.viewTree.elements.length; i++) {
            const viewElement = this.viewTree.elements[i];
            const origin = getCenter(viewElement);
            const rotatedPosition = rotatePosition(position, origin, viewElement.rotationZ!);

            if (N.isElementVisibleAtTime(viewElement, this.time)
                && N.positionIsInBounds(rotatedPosition, viewElement)) {
                return viewElement;
            }
        }
    }

    findAllElementAtPosition(element: N.OneOfGroupNode, position: IPosition): N.OneOfElements[] | [] {
        const elementsAtPosition: N.OneOfElements[] = [];
        for (let i = 0; i < this.viewTree.elements.length; i++) {
            const viewElement = this.viewTree.elements[i];
            const origin = getCenter(viewElement);
            const rotatedPosition = rotatePosition(position, origin, viewElement.rotationZ!);

            if (N.isElementVisibleAtTime(viewElement, this.time)
                && N.positionIsInBounds(rotatedPosition, viewElement)) {
                elementsAtPosition.push(viewElement);
            }
        }
        return elementsAtPosition;
    }

    clearElementHandlers(): void {
        for (const element of this.viewTree.elements) {
            const styleRule = this.styleRules.get(element.elementCid!)!;
            styleRule.current.styleRule.style.willChange = '';
            styleRule.previous.styleRule.style.willChange = '';
            if (element.__rootElement) {
                element.__rootElement.removeEventListener('click', this.onClick);
            }
        }
        this.rootElement?.removeEventListener('click', this.onClick);
    }

    destroy(): void {
        this.emit('destroy');
        if (this.styleElement && this.styleElement.parentNode) {
            (this.styleElement.parentNode as HTMLElement).removeChild(this.styleElement);
        }
        delete this.styleElement;
        this.styleSheet = undefined as any;
        this.clearElementHandlers();
        this.styleRules.clear();

        if (this.feedStore) {
            this.feedStore.destroy();
            this.feedStore.off('dataChanged', this.updateViewElementValues);
        }
        for (let i = 0; i < this.viewTree.elements.length; i++) {
            const viewElement = this.viewTree.elements[i];
            if ((viewElement.kind === N.NodeKind.Text || viewElement.kind === N.NodeKind.Button) && viewElement.__richTextRenderer) {
                viewElement.__richTextRenderer.destroy();
                delete viewElement.__richTextRenderer;
            }
        }
        if (this['WidgetRenderer']) {
            this['WidgetRenderer'].destroy();
        }
        this.storedViewElements.clear();
        this.destroyed = true;
        this.clearEvents();
    }

    createViewElementFromDataElement<T>(dataElement: N.OneOfDataElements): T {
        const viewElement = N.createViewElement(dataElement);
        this.storedViewElements.set(dataElement.id, viewElement);
        this.setViewElementValues(dataElement, this.time, /*setStyle*/false);
        N.setBoundingBox(viewElement, this.canvasSize);
        return viewElement;
    }

    setZIndexStyleOnAllNodes(): void {
        N.forEachElement(this.viewTree, (element) => {
            const styleRule = this.styleRules.get('p-' + element.elementCid!)!.current.styleRule;
            styleRule.style.zIndex = this.getZIndex(element) + '';
        }, /*atTime*/undefined, true);
    }

    bringElementToTop(element: N.OneOfElements): void {
        N.forEachElement(this.viewTree, (elementInTree) => {
            if (element === elementInTree) {
                const styleRule = this.styleRules.get('p-' + element.elementCid!)!.current.styleRule;
                styleRule.style.zIndex = '10000000000';
            }
        }, /*atTime*/undefined, true);
    }

    getDefaultStyle(element: N.OneOfElements): any {
        const defaultStyles = {
            visibility: N.isElementVisibleAtTime(element, this.time) ? 'visible' : 'hidden',
            ...this.getPositionAndSizeStyle(element),
            ...(typeof element.radius === 'number' ? { borderRadius: `${element.radius}px` } : null),
            ...(typeof element.opacity === 'number' ? { opacity: `${element.opacity}` } : null)
        } as any;

        return defaultStyles;
    }

    setZoom(): void {
        for (const element of this.viewTree.elements) {
            if (N.isTextNode(element)) {
                element.__richTextRenderer.rerender();
            }
        }
    }

    /**
     * Get z-index of element. If element not yet added to viewTree this will return undefined
     * @param element
     */
    private getZIndex(element: N.OneOfElements): number | undefined {
        const elements = this.viewTree.elements;
        const index = elements.indexOf(element);
        return index !== -1 ? elements.length - index : undefined;
    }

    private getPerspectiveStyle(element: N.OneOfElements): any {

        const x = 100 * (element.x + element.width / 2) / this.creativeDocument.width;
        const y = 100 * (element.y + element.height / 2) / this.creativeDocument.height;
        const perspective = Math.max(element.width, element.height) * 2;
        const zIndex = this.getZIndex(element);

        const styles = {
            perspectiveOrigin: `${toFixedDecimal(x)}% ${toFixedDecimal(y)}%`,
            perspective: `${perspective}px`
        } as any;

        if (zIndex !== undefined) {
            styles.zIndex = zIndex;
        }
        return styles;
    }

    getPositionAndSizeStyle(element: N.OneOfElements): IPositionStyle & ISizeStyle {
        return {
            width: element.width + 'px',
            height: element.height + 'px',
            transform: this.getTransformMatrix(element),
        };
    }

    getTransformString(styles: N.OneOfElements, exludeTranslate: boolean = false): string {

        const scale = this.getScale(styles);
        let transformString = exludeTranslate ? '' : `translate(${styles.x}px, ${styles.y}px)`;

        if (styles.rotationZ) {
            transformString += ` rotateZ(${styles.rotationZ}rad)`;
        }
        if (styles.rotationY) {
            transformString += ` rotateY(${styles.rotationY}rad)`;
        }
        if (styles.rotationX) {
            transformString += ` rotateX(${styles.rotationX}rad)`;
        }


        transformString += ` scaleX(${scale.x})`;
        transformString += ` scaleY(${scale.y})`;

        return transformString;
    }

    getTransformMatrix(styles: N.OneOfElements): string {

        const scale = this.getScale(styles);
        const matrix = new Matrix();
        matrix.translate(styles.x!, styles.y!);

        if (styles.rotationZ) {
            matrix.rotateZ(styles.rotationZ);
        }
        if (styles.rotationY) {
            matrix.rotateY(styles.rotationY);
        }
        if (styles.rotationX) {
            matrix.rotateX(styles.rotationX);
        }

        matrix.scale(scale.x, scale.y);

        return matrix.toCSS();
    }

    private getScale(styles: N.OneOfElements): IPosition {
        return {
            x: (typeof styles.scaleX === 'number' ? styles.scaleX : 1) * (styles.mirrorX ? -1 : 1),
            y: (typeof styles.scaleY === 'number' ? styles.scaleY : 1) * (styles.mirrorY ? -1 : 1)
        };
    }

    // private getTransformOriginPixels(origin: Position, size: Size): Position {
    //     return {
    //         x: origin.x * size.width / 100,
    //         y: origin.y * size.height / 100,
    //     };
    // }

    // /**
    //  * When rotating an element transform origin needs to be adjusted. This will return the distance to the original position
    //  * @param element
    //  */
    // private getTransformOriginTranslation(element: N.OneOfElements, matrix: CSSTransform): Position3D {
    //     const mainOrigin = this.getTransformOriginPixels({ x: 50, y: 50 }, element);
    //     const transitionOrigin = this.getTransformOriginPixels({ x: element.originX!, y: element.originY! }, element);

    //     const data = element.__data as N.OneOfDataElements;
    //     const defaultRotation: Position3D = {
    //         x: +N.getMainPropertyKeyframesOnElement(data, 'rotationX')[0].value || 0,
    //         y: +N.getMainPropertyKeyframesOnElement(data, 'rotationY')[0].value || 0,
    //         z: +N.getMainPropertyKeyframesOnElement(data, 'rotationZ')[0].value || 0
    //     };

    //     const defaultPosition = {
    //         x: element.width * (transitionOrigin.x - 50) / 100,
    //         y: element.height * (transitionOrigin.y - 50) / 100,
    //         z: 0
    //     };

    //     const transitionRotation = matrix.getRotationVector();

    //     const transitionRotation2 = {
    //         x: element.rotationX || 0,
    //         y: element.rotationY || 0,
    //         z: element.rotationZ || 0,
    //     };

    //     // Get coordinates for default position
    //     const rotatedPositionDefault = rotatePosition3D(defaultPosition, defaultRotation);

    //     // Get coordinates for second position
    //     const rotatedPositionTransition = rotatePosition3D(defaultPosition, transitionRotation2);

    //     return {
    //         x: rotatedPositionDefault.x - rotatedPositionTransition.x,
    //         y: rotatedPositionDefault.y - rotatedPositionTransition.y,
    //         z: rotatedPositionDefault.z - rotatedPositionTransition.z
    //     };
    // }

    removeStyle(rule: CSSStyleRule): void {
        for (let i = this.styleSheet.cssRules.length - 1; i >= 0; i--) {
            if ((this.styleSheet.cssRules[i] as any).selectorText === rule.selectorText) {
                this.styleSheet.deleteRule(i);
                break;
            }
        }
    }

    private removeStyles(): void {
        this.styleRules.clear();
        const count = this.styleSheet && this.styleSheet.cssRules ? this.styleSheet.cssRules.length : 0;
        for (let i = count - 1; i > 2; i--) {
            this.styleSheet.deleteRule(i);
        }
    }

    private insertStyles(): void {

        // Perspective div
        this.styleSheet.insertRule(`
            #${this.scopeId} > div {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                height: 100%;
                perspective: 200px;
                perspective-origin: 50% 50%;
                pointer-events: none;
            }`
            , this.styleSheet.cssRules.length);

        // Element div
        this.styleSheet.insertRule(`
            #${this.scopeId} > div > div {
                position: absolute;
                pointer-events: auto;
            }`
            , this.styleSheet.cssRules.length);

        // Text div
        this.styleSheet.insertRule(`
            #${this.scopeId} > div > div > div {
                position: relative;
                z-index: 1;
            }`
            , this.styleSheet.cssRules.length);
    }

    insertStyle(cid: string, style: ICSSProperties): void {
        let selector: string;
        if (cid !== this.scopeId) {
            selector = `#${this.scopeId} #${cid}`;
        }
        else {
            selector = '#' + cid;
        }
        let rules = '';
        for (const rule in style) {
            if (style[rule]) {
                rules += rule.replace(/([A-Z])/g, (m) => {
                    return '-' + m.toLowerCase();
                }) + ':' + style[rule] + ';';
            }
        }

        // Insert rules for element div
        this.styleSheet.insertRule(`${selector}{${rules}}`, this.styleSheet.cssRules.length);
        const styleRule = this.styleSheet.cssRules[this.styleSheet.cssRules.length - 1] as CSSStyleRule;
        const styles = this.getStyleValues(styleRule);
        this.styleRules.set(cid, {
            previous: {
                styleRule: styleRule,
                rawStyleValue: styles
            },
            current: {
                styleRule: styleRule,
                rawStyleValue: styles
            }
        });
        const element = this.htmlElements.get(cid)!;
        element.setAttribute('id', cid);
    }

    private getStyleValues(styleRule: CSSStyleRule): CSSStyleDeclaration {
        const styles = {} as CSSStyleDeclaration;
        for (let i = 0; i < styleRule.style.length; i++) {
            const style = styleRule.style[i];
            styles[style] = styleRule.style[styleRule.style[i]];
        }
        return styles;
    }

    private insertPerspectiveStyle(id: string, style?: ICSSProperties): void {
        let selector: string;
        if (id !== this.scopeId) {
            selector = `#${this.scopeId} #${id}`;
        }
        else {
            selector = '#' + id;
        }
        let rules = '';
        if (style) {
            for (const rule in style) {
                if (style[rule]) {
                    rules += rule.replace(/([A-Z])/g, (m) => {
                        return '-' + m.toLowerCase();
                    }) + ':' + style[rule] + ';';
                }
            }
        }
        // Insert rules for perspective
        this.styleSheet.insertRule(`${selector}{${rules}}`, this.styleSheet.cssRules.length);
        const styleRule = this.styleSheet.cssRules[this.styleSheet.cssRules.length - 1] as CSSStyleRule;
        const styles = this.getStyleValues(styleRule);
        this.styleRules.set(id, {
            previous: {
                styleRule: styleRule,
                rawStyleValue: styles
            },
            current: {
                styleRule: styleRule,
                rawStyleValue: styles
            }
        });
    }

    createElement(name: string, element: N.OneOfElements): HTMLElement {
        const htmlElement = this.browserDocument.createElement(name);
        this.htmlElements.set(element.elementCid!, htmlElement);
        const perspectiveElement = this.htmlElements.get('p-' + element.elementCid);
        if (perspectiveElement) {
            perspectiveElement.appendChild(htmlElement);
        }
        // this.rootElement.insertBefore(htmlElement, this.rootElement.firstChild);
        return htmlElement;
    }

    createPerspectiveElement(element: N.OneOfElements): HTMLDivElement {
        const id = 'p-' + element.elementCid!;
        const htmlElement = this.browserDocument.createElement('div');
        const style = this.getPerspectiveStyle(element);
        htmlElement.setAttribute('id', id);
        this.insertPerspectiveStyle(id, style);
        this.htmlElements.set(id, htmlElement);
        this.rootElement.insertBefore(htmlElement, this.rootElement.firstChild);
        return htmlElement;
    }

    injectFontFace(font: IFontStyle, override?: boolean): void {
        if (this.env.STUDIO_JS || override) {
            this.insertFontFace(font.id, font, this.browserDocument, override);
        }
    }

    private insertFontFace(fontId: string, fontStyle: IFontStyle, document: Document = window.document, override?: boolean): void {

        // Don't create a new font-face if it already exists
        if (!fontId || this.browserDocument.getElementById(fontId)) {
            if (override) {
                const style = this.browserDocument.getElementById(fontId)!;
                style.parentNode!.removeChild(style);
            }
            else {
                return;
            }
        }

        const fontface = `
            @font-face {
                font-family: "${fontStyle.id}";
                font-weight: ${fontStyle.weight};
                font-style: ${fontStyle.style};
                src: url("${fontStyle.src.replace('storage-emulator', 'localhost')}") format("woff");
            }
        `;

        const fontFaceStyleNode = this.browserDocument.createElement('style');
        fontFaceStyleNode.id = fontId;
        fontFaceStyleNode.appendChild(this.browserDocument.createTextNode(fontface));
        const preloader = this.browserDocument.createElement('div');

        preloader.setAttribute('style', `
            position: absolute;
            opacity: 0;
            top: -100px;
            font-family: "${fontStyle.id}";
            font-weight: ${fontStyle.weight};
            font-style: ${fontStyle.style};
        `);
        preloader.innerHTML = 'a';
        this.browserDocument.head!.appendChild(fontFaceStyleNode);
        this.browserDocument.body.appendChild(preloader);
    }

    private getSVGBackgroundChecksum(element: N.ISVGBackgroundNode): string {
        const shadows = element.shadows || [];
        let raw = `${element.width}x${element.height}-${element.radius || 0}-`;

        if (element.imageAsset) {
            raw += element.imageAsset.id;
        }
        if (element.fill) {
            raw += element.fill.toString();
        }
        if (shadows.length) {
            raw += boxShadowsCSSValue(shadows);
        }
        if (element.border) {
            raw += borderCSSValue(element.border);
        }
        if (element.imageAsset) {
            raw += element.imageAsset.id;
        }
        if (element.kind === N.NodeKind.Image && (element as N.IImageElement).fitOption) {
            if ((element as N.IImageElement).fitPositionX) {
                raw += `${(element as N.IImageElement).fitPositionX}`;
            }
            if ((element as N.IImageElement).quality) {
                raw += `${(element as N.IImageElement).quality}`;
            }
            if ((element as N.IImageElement).fitPositionY) {
                raw += `${(element as N.IImageElement).fitPositionY}`;
            }
            raw += `${(element as N.IImageElement).fitOption}`;
        }
        if (element.kind === N.NodeKind.Image && (element as N.IImageElement).feed) {
            const el = (element as N.IImageElement);
            const feedValue = this.feedStore!.getFeedValue(el.feed!, (el as N.IImageElement).id, el as N.IElementNode).value;
            if (el.feed) {
                raw += `${feedValue}-${el.feed!.path}-${el.feed!.step.start}`;
            }

        }

        return checksum(raw);
    }

}

interface IPositionStyle {
    transform: string;
    transformOrigin?: string;
    backfaceVisibility?: string;
}

interface ISizeStyle {
    width: string;
    height: string;
}

export interface IDependency {
    name: string;
    constructor: new (...args: any[]) => any;
    arguments: any[];
}
